/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import useGetEventDate from '../../common/hooks/useGetEventDate';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RestoreIcon from '@mui/icons-material/Restore';
import { Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import { QueryFunctionContext, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import * as constants from '../../common/constants';
import useAbortController from '../../common/hooks/useAbortController';
import useGetBatchesList from '../../common/hooks/useGetBatchesList';
import { isStrEmptyOrNull } from '../../common/UtilityFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanHistoryContext } from '../../contexts/LoanHistoryTransactionContext';
import { Sorted, Tables } from '../../pages/LoanHistoryPage';
import axiosCall from '../../services/axios';
import LoadingModal from '../modals/LoadingModal';
import ReverseModal from '../modals/ReverseModal';
import NumericCell from '../NumericCell';
import useGetServicingPayment from './../../common/hooks/useGetServicingPayment';
import Style from './../../css/LoanHistory.module.css';

export type Payment = {
  open: boolean;
  id: string;
  date: string;
  dateReceived: string;
  paidTo: string;
  transactionCode: string;
  mcdTransTypeAbrev: string;
  mcdTransSuffix: string;
  description: string;
  principal: string;
  balance: string;
  interest: string;
  escrow: string;
  escrowBalance: string;
  lateCharge: string;
  misc: string;
  total: string;
  checkNo: string;
  payee: string;
  posted: boolean;
  canReverse: boolean;
  miscPayment: MiscPayment;
};

export type ServicingPayment = {
  dateReceived: number;
  mcdTransTypeAbrev: string;
  mcdTransSuffix: string;
  description: string;
  checkNo: string;
  payee: string;
  posted: boolean;
  uid: string;
  postDate: number;
  mcdTransactionId: string;
  accountTypeKeyword: string;
  taxesPaidAlloc: number;
  reqUserId: string;
  interestAlloc: number;
  mcdTransTypeKeyword: string;
  policyNo: string;
  otherSuspenseAlloc: number;
  targetBalance: number;
  twinSecondsTransactionId: string;
  payeeTwo: string;
  reqLastName: string;
  item: string;
  mcdTransCode: string;
  impTypeDescription: string;
  angelPointsTransactionId: string;
  checkDepositDate: number;
  feeNoKeyword: string;
  active: true;
  sourceBalance: number;
  feeNo: string;
  pointsCategoryKeyword: string;
  targetCurrencyKeyword: string;
  impGroupDescription: string;
  lossDraftAlloc: number;
  transTypeDescription: string;
  exchangeDate: number;
  reqFirstName: string;
  prinCurtailReqstd: number;
  agencyIntSubsidyAlloc: number;
  servFeeAlloc: number;
  sourceCurrency: string;
  corpAdvAcrdAlloc: number;
  payeeThree: string;
  impTypeKeyword: string;
  transactionHash: string;
  escrowAlloc: number;
  fileStatusDescription: string;
  lateChrgAlloc: number;
  subsidyName: string;
  interestReqstd: number;
  feeNoDescription: string;
  insurancePaidAlloc: number;
  loanBelongsTo: string;
  sourceAmount: number;
  assessmntWvdAlloc: number;
  pointsCategoryDescription: string;
  timestamp: number;
  approver: string;
  reqSuffixName: string;
  rvrsBatch: string;
  packageTier: string;
  routingNo: string;
  fromUserId: string;
  cnt: string;
  afterPostUpbBal: number;
  principalAlloc: number;
  escrow3Alloc: number;
  suspenseUsedAlloc: number;
  sourceCurrencyKeyword: string;
  subsidyCnt: string;
  repairAlloc: number;
  impGroupKeyword: string;
  mcdTransTypeDescription: string;
  assessmentAlloc: number;
  lateChrgWvdAlloc: number;
  unitNo: string;
  prinCurtlSrcDescription: string;
  sourceCurrencyDescription: string;
  transactionDescription: string;
  transactionPayee: string;
  extraEscrowReqstd: number;
  impGroup: string;
  prinAdjusted: number;
  prinCurtlSrc: string;
  afterPostEscrowBal: number;
  assessmentsReqstd: number;
  reqMiddleName: string;
  accountTypeDescription: string;
  exchangeRate: number;
  mcdTransSuffixDescription: string;
  prinCurtlSrcKeyword: string;
  deferPrincipalAlloc: number;
  lateChrgAcrdAlloc: number;
  reqUserType: string;
  transTypeKeyword: string;
  batchNo: string;
  targetCurrency: string;
  accountType: string;
  history: string;
  suspenseReqstd: number;
  assessmntAcrdAlloc: number;
  corpAdvAlloc: number;
  transType: string;
  agencyPrinSubsidyAlloc: number;
  subsidyNameDescription: string;
  assessorCode: string;
  checkNoLeadingZeroes: string;
  recId: string;
  loan: string;
  corpAdvanceReqstd: number;
  purpose: string;
  purposeKeyword: string;
  prinCurtlAlloc: number;
  toUserId: string;
  deferPaymentAlloc: number;
  feeCnt: string;
  MIPPaidAlloc: number;
  targetCurrencyDescription: string;
  fileStatus: string;
  accountNo: string;
  negAmAlloc: number;
  pointsCategory: string;
  purposeDescription: string;
  escrow2Alloc: number;
  reqUserTypeKeyword: string;
  reverseTrn1TransNo: string;
  reqUserTypeDescription: string;
  amount: number;
  fileStatusKeyword: string;
  suspenseAlloc: number;
  targetAmount: number;
  impType: string;
  principalReqstd: number;
  subsidyNameKeyword: string;
  corpAdvWvdAlloc: number;
  renewalDate: number;
  transNo: string;
  transactionDate: number;
  lateChargeReqstd: number;
  mcdTransSuffixKeyword: string;
  deferInterestAlloc: number;
  parcelId: string;
  buydownAlloc: number;
  subSuspenseAlloc: number;
  mcdTransType: string;
  comment: string;
  rvrsCnt: string;
  paymentDate: number;
};

export type MiscPayment = {
  assessmentAlloc: number;
  assessmntAcrdAlloc: number;
  assessmntWvdAlloc: number;
  corpAdvAcrdAlloc: number;
  corpAdvAlloc: number;
  corpAdvWvdAlloc: number;
  deferInterestAlloc: number;
  deferPaymentAlloc: number;
  deferPrincipalAlloc: number;
  escrow2Alloc: number;
  escrow3Alloc: number;
  lateChrgAcrdAlloc: number;
  lateChrgWvdAlloc: number;
  lossDraftAlloc: number;
  negAmAlloc: number;
  otherSuspenseAlloc: number;
  prinCurtlAlloc: number;
  prinAdjusted: number;
  repairAlloc: number;
  servFeeAlloc: number;
  subSuspenseAlloc: number;
  suspenseAlloc: number;
  suspenseUsedAlloc: number;
  agencyPrinSubsidyAlloc: number;
  agencyIntSubsidyAlloc: number;
  batchNo: string;
  cnt: string;
};

type LoanHistoryTransactionFormProps = {
  loanNumber: string;
  selectedTable: string;
  selectedSort: string;
  showFullMigrationHistory: boolean;
};

type DataProps = {
  label: string;
  value: number;
};

type TextDataProps = {
  label: string;
  value: string;
};

const LoanHistoryTransactionForm = memo((props: LoanHistoryTransactionFormProps) => {
  const dateFormatConst = 'MM/DD/YYYY';
  const [loading, setLoading] = useState(false);
  const {
    servicingLoanPayments,
    servicingErrorMessages,
    loadingServicing,
    refetch: refetchServicingPayments,
  } = useGetServicingPayment(props.loanNumber);
  const { filteredBatches, batchesErrorMessages, loadingBatches } = useGetBatchesList();
  const [openReverseModal, setOpenReverseModal] = useState(false);
  const [revId, setRevId] = useState('');
  const payeeMapRef = useRef(new Map());
  const { token, fullAccess } = useAuthContext();
  const { isPaymentApplicableBasedOnMigration, getRemovedDuplicatesServPayments } = useLoanHistoryContext();
  const [openRowIds, setOpenRowIds] = useState<Set<string>>(new Set());
  const { getEventDateInfo } = useGetEventDate();

  const { get, reset } = useAbortController();
  const { enqueueSnackbar } = useSnackbar();

  const openCollapableRow = useCallback(
    (id: string) => {
      const newSet = new Set(openRowIds);
      if (openRowIds.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      setOpenRowIds(newSet);
    },
    [openRowIds],
  );

  const openRevModal = (id: string) => {
    //open the Reverse Transaction modal
    setRevId(id);
    setOpenReverseModal(true);
  };

  const closeRevModal = () => {
    //close the Reverse Transaction modal
    setOpenReverseModal(false);
  };

  const reverseTransaction = async (transaction: string, batchNumber: string, description: string) => {
    setLoading(true);
    let row = servicingLoanPayments.find((item) => item.recId === revId);
    // first create the reverse transaction if a count was not returned then an error occured stop processing
    let count = await createTransaction(transaction, description, batchNumber, row.batchNo, row.cnt);

    if (count) {
      //creating the transaction was successfull now post it
      await postTransaction(batchNumber, count);
    }
    await refetchServicingPayments();
    setLoading(false);
  };

  const createTransaction = async (
    transaction: string,
    description: string,
    batchNumber: string,
    origBatchNumber: string,
    origCount: string,
  ) => {
    if (!token || !batchNumber || !props.loanNumber) return '';
    const url = process.env.REACT_APP_CREATE_TRANSACTION.replace('?', batchNumber) + props.loanNumber + '/add';

    const controller = get();
    const config = { signal: controller.signal };

    const formData = {
      mcdTransType: transaction,
      description: description,
      rvrsBatch: origBatchNumber,
      rvrsCnt: origCount,
    };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    try {
      const response = await axiosCall({ url: url, method: 'post', data: formData }, config, headers);
      enqueueSnackbar('Reverse Transaction Created', { variant: 'success' });
      return response.cnt;
    } catch (error) {
      console.error('Error Creating new Transaction: ' + error);
      enqueueSnackbar('Error Creating new Transaction.', { variant: 'error' });
    }
    return '';
  };

  const postTransaction = async (batchNumber: string, count: string) => {
    const url = process.env.REACT_APP_POST_TRANSACTION.replace('?', batchNumber) + count;

    const controller = get();
    const config = { signal: controller.signal };

    const formData = {
      posted: true,
    };

    const headers = {
      'Session-Token': token,
      'Content-Type': 'application/json',
    };

    try {
      const response = await axiosCall({ url: url, method: 'post', data: formData }, config, headers);
      enqueueSnackbar('Reverse Transaction Posted', { variant: 'success' });

      return response.cnt;
    } catch (error) {
      console.error('Error Posting new Transaction: ' + error);
      enqueueSnackbar('Error Posting new Transaction.', { variant: 'error' });
    }
  };

  // Judith Espinal - 04/01/2026 - SQC-590: Use filteredBatches from hook (pre-filtered by postedSource, keyword, and amountPosted)
  const getBatchArray = useCallback(() => {
    const array = [];
    filteredBatches.forEach((item) => {
      array.push(item.batchNo);
    });
    array.reverse();
    return array;
  }, [filteredBatches]);

  const batchArray = useMemo(() => getBatchArray(), [filteredBatches]);

  const retrievePayeeDetails = useCallback(
    async (loanBelongsTo: string, payeeOne: string, payeeTwo: string, payeeThree: string) => {
      if (!token || !props.loanNumber) return '';
      if (isStrEmptyOrNull(payeeOne) && isStrEmptyOrNull(payeeTwo) && isStrEmptyOrNull(payeeThree)) {
        return loanBelongsTo;
      }
      let payeeStr = '';

      const payees = [payeeOne, payeeTwo, payeeThree];

      try {
        for (let payee of payees) {
          if (!isStrEmptyOrNull(payee)) {
            let payeeName = '';
            if (!isStrEmptyOrNull(payeeStr)) {
              payeeStr += ', ';
            }
            payeeStr += payee;

            // if we haven't seen the payee yet, retrieve the name and add it to the
            // payeeMap so that we do not send unnecessary API requests for repeat payees.
            if (!payeeMapRef.current.has(payee)) {
              // get the result from Misc Payee Master
              const config = reset();
              const headers = {
                'Session-Token': token,
              };
              const payeeRequest = {
                url: `${process.env.REACT_APP_PAYEE}${payee}?loan=${props.loanNumber}`,
                method: 'GET',
              };
              let response = await axiosCall(payeeRequest, config, headers);

              if (response) {
                const payeeData = { ...response };

                // if Payee is the borrower, Payee record will be inactive but still have a name.
                if (payeeData?.active || payee === constants.borrowerPayeeNo) {
                  if (payeeData?.payee?.nameOne) {
                    // use payee.nameOne if the field is set.
                    payeeName = payeeData?.payee?.nameOne;
                  } else if (payeeData?.assessorCode) {
                    // if assessorCode is set, retrieve the Assessor data.
                    // use nameOne if the field is set.
                    const assessorRequest = {
                      url: `${process.env.REACT_APP_ASSESSOR}${payeeData?.assessorCode}`,
                      method: 'GET',
                    };
                    response = await axiosCall(assessorRequest, config, headers);
                    const assessorData = { ...response };

                    if (assessorData?.nameOne) payeeName = assessorData?.nameOne;
                  } else if (payeeData?.companyCode) {
                    // if companyCode is set, retrieve the Company data
                    // use name1 if the field is set.
                    const companyRequest = {
                      url: `${process.env.REACT_APP_COMPANY}${payeeData?.companyCode}`,
                      method: 'GET',
                    };
                    response = await axiosCall(companyRequest, config, headers);
                    const companyData = { ...response };

                    if (companyData?.name1) payeeName = companyData?.name1;
                  }
                }
              }
              payeeMapRef.current.set(payee, payeeName);
            } else {
              payeeName = payeeMapRef.current.get(payee);
            }
            if (!isStrEmptyOrNull(payeeName)) {
              payeeStr += ` - ${payeeName}`;
            }
          }
        }
        return payeeStr;
      } catch (error) {
        console.error('Error retrieving Payee data: ' + error);
        enqueueSnackbar('Error retrieving Payee.', { variant: 'error' });
      }
    },
    [props.loanNumber, payeeMapRef, reset, token],
  );

  const getFilteredServicingPayments = useCallback(async (): Promise<Payment[]> => {
    const payments: Payment[] = [];

    const migrationEvent = await getEventDateInfo(props.loanNumber, 'S7H');

    // Retain all raw posted transactions for relationships that may reference records removed during duplicate filtering
    const allPostedPayments = servicingLoanPayments.filter(
      (payment) => payment.mcdTransType !== '99' && payment.posted === true,
    );

    const removedDuplicatesServPayments = getRemovedDuplicatesServPayments(servicingLoanPayments);

    // Limit displayed Loan History processing to deduplicated posted transactions.
    const postedPayments = removedDuplicatesServPayments.filter(
      (pmt) => pmt.mcdTransType != '99' && pmt.posted === true,
    );

    const filteredPaymentList = props.showFullMigrationHistory
      ? allPostedPayments
      : postedPayments.filter((pmt) => {
          if (isPaymentApplicableBasedOnMigration(pmt, migrationEvent)) {
            return true;
          }

          // Reversal transactions should be included when their original transaction is applicable to the migration date.
          // Search the raw servicing payments because the original transaction may have been removed during duplicate filtering
          if (pmt.mcdTransType === '04') {
            const originalPayment = servicingLoanPayments.find(
              (payment) => payment.batchNo === pmt.rvrsBatch && payment.cnt === pmt.rvrsCnt,
            );

            return (
              originalPayment !== undefined &&
              originalPayment.posted === true &&
              originalPayment.mcdTransType !== '99' &&
              isPaymentApplicableBasedOnMigration(originalPayment, migrationEvent)
            );
          }

          return false;
        });

    //sort the payments based on postDate this will go from oldest to newest
    filteredPaymentList.sort((item1, item2) => {
      return item1.postDate - item2.postDate;
    });
    const paymentDateArray = [];
    paymentDateArray.push(0);
    var paymentDate = 0;

    for (let item of filteredPaymentList) {
      if (/* item.mcdTransSuffix !== 'V' &&*/ item.mcdTransType !== '99' && item.posted === true) {
        const other =
          item.assessmentAlloc +
          item.assessmntAcrdAlloc -
          item.assessmntWvdAlloc +
          item.corpAdvAcrdAlloc +
          item.corpAdvAlloc +
          item.corpAdvWvdAlloc +
          item.deferInterestAlloc +
          item.deferPaymentAlloc +
          item.deferPrincipalAlloc +
          item.escrow2Alloc +
          item.escrow3Alloc +
          item.lossDraftAlloc +
          item.negAmAlloc +
          item.otherSuspenseAlloc +
          item.repairAlloc +
          item.subSuspenseAlloc +
          item.suspenseAlloc +
          item.suspenseUsedAlloc +
          item.agencyPrinSubsidyAlloc +
          item.agencyIntSubsidyAlloc;

        const totPrin = item.principalAlloc + item.prinAdjusted;
        const totLate = item.lateChrgAcrdAlloc + item.lateChrgWvdAlloc + item.lateChrgAlloc;

        // Check every raw representation of the displayed transaction so a reversal tied to a duplicate record still prevents the transaction from being reversed again
        const matchingTransactionRecords = !isStrEmptyOrNull(item.mcdTransactionId)
          ? allPostedPayments.filter((payment) => payment.mcdTransactionId === item.mcdTransactionId)
          : [item];

        const reversedList = allPostedPayments.filter((match) =>
          matchingTransactionRecords.some(
            (payment) => match.rvrsBatch === payment.batchNo && match.rvrsCnt === payment.cnt,
          ),
        );

        const canReverse =
          fullAccess &&
          item.mcdTransType !== '03' &&
          item.mcdTransType !== '04' &&
          item.posted === true &&
          reversedList.length === 0 &&
          item.mcdTransSuffix !== 'V' &&
          !(item.mcdTransType === '01' && item.paymentDate) &&
          (item.mcdTransType != '2Q' || (item.description != 'Subsidy' && item.description != 'Reverse Subsidy'));

        if (item.paymentDate && item.mcdTransType !== '03' && item.mcdTransType !== '04' && item.mcdTransType !== '01')
          paymentDateArray.push(item.paymentDate);
        else if (item.paymentDate) paymentDateArray.pop();
        paymentDate = paymentDateArray[paymentDateArray.length - 1];
        const payeeStr = item.transactionPayee
          ? item.transactionPayee
          : await retrievePayeeDetails(item.loanBelongsTo, item.payee, item.payeeTwo, item.payeeThree);
        payments.push({
          open: false,
          id: item.recId,
          date: item.postDate ? dayjs(item.postDate * 1000).format(dateFormatConst) : '',
          dateReceived: item.dateReceived ? dayjs(item.dateReceived * 1000).format(dateFormatConst) : '',
          paidTo: paymentDate ? dayjs(paymentDate * 1000).format(dateFormatConst) : '',
          transactionCode: item.mcdTransCode,
          mcdTransTypeAbrev: item.mcdTransTypeAbrev,
          mcdTransSuffix: item.mcdTransSuffix,
          description: item.transactionDescription,
          principal: totPrin.toString(),
          balance: item.afterPostUpbBal.toFixed(2),
          interest: item.interestAlloc.toFixed(2),
          escrow: item.escrowAlloc.toFixed(2),
          escrowBalance: item.afterPostEscrowBal.toFixed(2),
          lateCharge: totLate.toString(),
          misc: other.toString(),
          total: item.amount.toFixed(2),
          checkNo: item.checkNo,
          payee: payeeStr,
          posted: item.posted,
          canReverse: canReverse,
          miscPayment: {
            assessmentAlloc: item.assessmentAlloc,
            assessmntAcrdAlloc: item.assessmntAcrdAlloc,
            assessmntWvdAlloc: item.assessmntWvdAlloc,
            corpAdvAcrdAlloc: item.corpAdvAcrdAlloc,
            corpAdvAlloc: item.corpAdvAlloc,
            corpAdvWvdAlloc: item.corpAdvWvdAlloc,
            deferInterestAlloc: item.deferInterestAlloc,
            deferPaymentAlloc: item.deferPaymentAlloc,
            deferPrincipalAlloc: item.deferPrincipalAlloc,
            escrow2Alloc: item.escrow2Alloc,
            escrow3Alloc: item.escrow3Alloc,
            lateChrgAcrdAlloc: item.lateChrgAcrdAlloc,
            lateChrgWvdAlloc: item.lateChrgWvdAlloc,
            lossDraftAlloc: item.lossDraftAlloc,
            negAmAlloc: item.negAmAlloc,
            otherSuspenseAlloc: item.otherSuspenseAlloc,
            prinCurtlAlloc: item.prinCurtlAlloc,
            prinAdjusted: item.prinAdjusted,
            repairAlloc: item.repairAlloc,
            servFeeAlloc: item.servFeeAlloc,
            subSuspenseAlloc: item.subSuspenseAlloc,
            suspenseAlloc: item.suspenseAlloc,
            suspenseUsedAlloc: item.suspenseUsedAlloc,
            agencyPrinSubsidyAlloc: item.agencyPrinSubsidyAlloc,
            agencyIntSubsidyAlloc: item.agencyIntSubsidyAlloc,
            batchNo: item.batchNo,
            cnt: item.cnt,
          } as MiscPayment,
        } as Payment);
      }
    }

    let filter = '';
    if (props.selectedTable === Tables.PAYMENT) filter = 'PAY';
    else if (props.selectedTable === Tables.TAX) filter = 'TAX';
    else if (props.selectedTable === Tables.HAZARD) filter = 'HAZ';
    else if (props.selectedTable === Tables.MIP) filter = 'MIP';
    else if (props.selectedTable === Tables.OTHER) filter = 'OTH';
    else if (props.selectedTable === Tables.ESCROW) filter = 'ESC';

    let suffixFilter = '';
    if (props.selectedTable === Tables.OTP) suffixFilter = 'OTP';

    const paymentsFiltered = payments.filter((payment) => {
      if (filter !== '') {
        return payment.mcdTransTypeAbrev.includes(filter);
      } else if (suffixFilter !== '') {
        //IMPORTANT NOTE: This suffix filter only accomodates OTP for 2 suffixes. If more suffixes or suffix filters are needed,
        //then implement the filter like the above abbreviation filter, utilizing the abrev for the QID. See API-1091 and SQC-418 for how-to.
        return payment.mcdTransSuffix === 'H' || payment.mcdTransSuffix === 'Z';
      } else {
        return payments;
      }
    });

    //we are already in oldest to newest no change for ascending order
    //reverse will display the payments newest to oldest to the user
    if (props.selectedSort === Sorted.DESC_ORDER) {
      paymentsFiltered.reverse();
    } else if (props.selectedSort === Sorted.PAYEE) {
      paymentsFiltered.sort((item1, item2) => {
        return item2.payee.localeCompare(item1.payee);
      });
    }
    return paymentsFiltered;
  }, [
    props.loanNumber,
    servicingLoanPayments,
    retrievePayeeDetails,
    props.selectedSort,
    props.selectedTable,
    props.showFullMigrationHistory,
    isPaymentApplicableBasedOnMigration,
    getEventDateInfo,
    getRemovedDuplicatesServPayments,
  ]);

  const {
    data: paymentList,
    isFetching: fetchingPaymentList,
    isLoading: loadingPaymentList,
  } = useQuery({
    queryKey: [
      props.loanNumber,
      token,
      'filteredServicingPayments',
      servicingLoanPayments,
      props.selectedSort,
      props.selectedTable,
      props.showFullMigrationHistory,
      getEventDateInfo,
    ],
    queryFn: ({ signal }: QueryFunctionContext) => getFilteredServicingPayments(),
    refetchOnWindowFocus: false,
  });

  if (!isStrEmptyOrNull(servicingErrorMessages)) {
    enqueueSnackbar(servicingErrorMessages, { variant: 'error' });
  }
  if (!isStrEmptyOrNull(batchesErrorMessages)) {
    enqueueSnackbar(batchesErrorMessages, { variant: 'error' });
  }

  return (
    <Fragment>
      {(loading || loadingServicing || loadingBatches || fetchingPaymentList || loadingPaymentList) && (
        <LoadingModal open={true} />
      )}
      <div className={Style.height}>
        <Table stickyHeader aria-label='sticky table'>
          <TableHead>
            <TableRow>
              <TableCell align='center' className={Style.headerCell} />
              <TableCell align='center' className={Style.headerCell}>
                Reverse?
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Date
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Received Date
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Paid-To Date
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Tr Code
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Description
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Principal
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Additional Principal
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Balance
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Interest
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Escrow
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Escrow Balance
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Late Charge
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Misc
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Total
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Check No
              </TableCell>
              <TableCell align='center' className={Style.headerCell}>
                Payee
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paymentList?.map((row, index) => (
              <Fragment>
                <TableRow key={row.id} className={`${index % 2 === 0 ? Style.even : Style.odd}`}>
                  <TableCell component='th' scope='row' align='center' className={Style.cell}>
                    <Tooltip title='Expand Row'>
                      <IconButton size='small' onClick={() => openCollapableRow(row.id)}>
                        {row.open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell align='center' className={Style.cell}>
                    {row.canReverse && (
                      <Tooltip title='Reverse Transaction'>
                        <IconButton size='small' onClick={() => openRevModal(row.id)}>
                          <RestoreIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align='center' className={Style.cell}>
                    {row.date}
                  </TableCell>
                  <TableCell align='center' className={Style.cell}>
                    {row.dateReceived}
                  </TableCell>
                  <TableCell align='center' className={Style.cell}>
                    {row.paidTo}
                  </TableCell>
                  <TableCell align='center' className={Style.cell}>
                    {row.transactionCode}
                  </TableCell>
                  <TableCell align='center' className={Style.cell}>
                    {row.description}
                  </TableCell>
                  <NumericCell value={row.principal} />
                  <NumericCell value={row?.miscPayment?.prinCurtlAlloc?.toFixed(2)} />
                  <NumericCell value={row.balance} />
                  <NumericCell value={row.interest} />
                  <NumericCell value={row.escrow} />
                  <NumericCell value={row.escrowBalance} />
                  <NumericCell value={row.lateCharge} />
                  <NumericCell value={row.misc} />
                  <NumericCell value={row.total} />
                  <TableCell align='center' className={Style.cell}>
                    {row.checkNo}
                  </TableCell>
                  <TableCell align='center' className={Style.cell}>
                    {row.payee}
                  </TableCell>
                </TableRow>
                <TableRow key={row.id + 'open'}>
                  <TableCell colSpan={16} style={{ paddingBottom: 0, paddingTop: 0 }} className={Style.cell}>
                    <Collapse in={openRowIds.has(row.id)} unmountOnExit>
                      <CollapsableRow {...row.miscPayment} />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
      <ReverseModal
        open={openReverseModal}
        onClose={closeRevModal}
        reverseTransaction={reverseTransaction}
        batches={batchArray}
      />
    </Fragment>
  );
});

export default LoanHistoryTransactionForm;

function CollapsableRow(props: MiscPayment) {
  return (
    <table className={Style.table}>
      <tbody>
        <tr>
          <td>
            <Data label='Principal Curtailment Alloc:' value={props.prinCurtlAlloc} />
          </td>
          <td>
            <Data label='Principal Adjusted:' value={props.prinAdjusted} />
          </td>
          <td>
            <Data label='Late Charge Accrued Alloc:' value={props.lateChrgAcrdAlloc} />
          </td>
          <td>
            <Data label='Late Charge Waived Alloc:' value={props.lateChrgWvdAlloc} />
          </td>
        </tr>
        <tr>
          <td>
            <Data label='Assessment Alloc:' value={props.assessmentAlloc} />
          </td>
          <td>
            <Data label='Assessment Accrued Alloc:' value={props.assessmntAcrdAlloc} />
          </td>
          <td>
            <Data label='Assessment Waived Alloc:' value={props.assessmntWvdAlloc} />
          </td>
          <td>
            <Data label='Corporate Advance Accrued Alloc:' value={props.corpAdvAcrdAlloc} />
          </td>
        </tr>
        <tr>
          <td>
            <Data label='Corporate Advance Alloc:' value={props.corpAdvAlloc} />
          </td>
          <td>
            <Data label='Corporate Advance Waived Alloc:' value={props.corpAdvWvdAlloc} />
          </td>
          <td>
            <Data label='Defer Interest Alloc:' value={props.deferInterestAlloc} />
          </td>
          <td>
            <Data label='Defer Payment Alloc:' value={props.deferPaymentAlloc} />
          </td>
        </tr>
        <tr>
          <td>
            <Data label='Defer Principal Alloc:' value={props.deferPrincipalAlloc} />
          </td>
          <td>
            <Data label='Escrow 2 Alloc:' value={props.escrow2Alloc} />
          </td>
          <td>
            <Data label='Escrow 3 Alloc:' value={props.escrow3Alloc} />
          </td>
          <td>
            <Data label='Loss Draft Alloc:' value={props.lossDraftAlloc} />
          </td>
        </tr>
        <tr>
          <td>
            <Data label='Negative Amortization Alloc:' value={props.negAmAlloc} />
          </td>
          <td>
            <Data label='Other Suspense Alloc:' value={props.otherSuspenseAlloc} />
          </td>
          <td>
            <Data label='Repair Alloc:' value={props.repairAlloc} />
          </td>
          <td>
            <Data label='Servicing Fee Alloc:' value={props.servFeeAlloc} />
          </td>
        </tr>
        <tr>
          <td>
            <Data label='SubSuspense Alloc:' value={props.subSuspenseAlloc} />
          </td>
          <td>
            <Data label='Suspense Alloc:' value={props.suspenseAlloc} />
          </td>
          <td>
            <Data label='Suspense Used Alloc:' value={props.suspenseUsedAlloc} />
          </td>
          <td>
            <Data label='Principal Subsidy Alloc:' value={props.agencyPrinSubsidyAlloc} />
          </td>
        </tr>
        <tr>
          <td>
            <Data label='Interest Subsidy Alloc:' value={props.agencyIntSubsidyAlloc} />
          </td>
          <td>
            <TextData label='Batch-Cnt:' value={props.batchNo + '-' + props.cnt} />
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function Data(props: DataProps) {
  return (
    <Fragment>
      <label className={Style.textSeperation}>{props.label}</label>
      <NumericFormat
        value={props.value}
        prefix={'$'}
        decimalScale={2}
        fixedDecimalScale
        thousandsGroupStyle='thousand'
        displayType='text'
        thousandSeparator=','
        className={Style.textSeperation}
      />
    </Fragment>
  );
}

function TextData(props: TextDataProps) {
  return (
    <Fragment>
      <label className={Style.textSeperation}>{props.label}</label>
      {props.value}
    </Fragment>
  );
}
